import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { withRouter, useHistory } from 'react-router-dom';
import moment from 'moment';
import Page from '../../components/Page/Page';
import { UserFilter } from '../../components';
import { NetworkContext } from '../../context/NetworkContext';
import {
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';

const STATUS_COLORS = {
  active: 'primary',
  expiring_soon: 'secondary',
  grace: 'default',
  blocked: 'default',
};

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'expiring_soon', label: 'Expiring Soon' },
  { value: 'grace', label: 'Grace' },
  { value: 'blocked', label: 'Blocked' },
];

const SUBSCRIPTION_CSV_HEADERS = [
  { label: 'User ID', key: 'user_profile_id' },
  { label: 'Name', key: 'name' },
  { label: 'Mobile Number', key: 'mobilenumber' },
  { label: 'Company Name', key: 'companyname' },
  { label: 'Email', key: 'email' },
  { label: 'City', key: 'city' },
  { label: 'District', key: 'district' },
  { label: 'State', key: 'state' },
  { label: 'Platform', key: 'platform' },
  { label: 'GSTIN', key: 'gstin' },
  { label: 'Plan', key: 'plan' },
  { label: 'Amount', key: 'amount' },
  { label: 'Valid From', key: 'valid_from' },
  { label: 'Valid Until', key: 'valid_until' },
  { label: 'Status', key: 'status' },
  { label: 'Is Trial', key: 'is_trial' },
  { label: 'Last Login', key: 'last_login' },
  { label: 'Member Since', key: 'member_since' },
];

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: '100%',
    padding: theme.spacing(3),
  },
  filterRow: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  filter: {
    minWidth: 200,
    marginTop: theme.spacing(1),
  },
  results: {
    marginTop: theme.spacing(2),
  },
}));

export const SubscriptionUsers = withRouter(() => {
  const classes = useStyles();
  const history = useHistory();
  const { sendNetworkRequest } = React.useContext(NetworkContext);
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [filterparams, setFilterparams] = useState({});
  const [masters, setMasters] = useState({});
  const [downloadusers, setDownloadusers] = useState([]);
  const [ispreparedownload, setIspreparedownload] = useState(false);

  function onFilter(filterobj) {
    setIspreparedownload(false);
    setDownloadusers([]);
    const next = { ...(filterobj || {}) };
    if (Object.prototype.hasOwnProperty.call(next, 'membership_status')) {
      setStatusFilter(next.membership_status || 'all');
      delete next.membership_status;
    }
    setFilterparams(prev => ({
      ...prev,
      ...next,
    }));
  }

  function onSearch(searchtext) {
    setIspreparedownload(false);
    setDownloadusers([]);
    setFilterparams(prev => ({
      ...prev,
      searchtext,
    }));
  }

  function onStatusChange(value) {
    setIspreparedownload(false);
    setDownloadusers([]);
    setStatusFilter(value);
  }

  async function loadUsers() {
    const result = await sendNetworkRequest('/admin/listsubscriptionusers', {}, {
      ...filterparams,
      status_filter: statusFilter === 'all' ? null : statusFilter,
    }, false);
    setUsers(result?.users || []);
  }

  async function onPreparedownload() {
    const result = await sendNetworkRequest('/admin/downloadsubscriptionusers', {}, {
      ...filterparams,
      status_filter: statusFilter === 'all' ? null : statusFilter,
    }, false);
    setDownloadusers([...(result?.rows || [])]);
    setIspreparedownload(true);
  }

  async function fetchmasters() {
    const commodities = await sendNetworkRequest('/admin/getparentcommodity', {}, {}, true);
    const master_commodities = [];
    (commodities?.module || []).forEach((item) => {
      master_commodities.push({
        value: item?.id,
        label: item?.name,
      });
    });
    const languages = await sendNetworkRequest('/admin/fetchmasterlanguage', {}, {}, false);
    const master_languages = [];
    (languages || []).forEach((item) => {
      master_languages.push({
        value: item?.id,
        label: item?.name,
      });
    });
    const usertypes = await sendNetworkRequest('/admin/fetchusertype', {}, {}, false);
    setMasters({
      commodity: master_commodities,
      languages: master_languages,
      usertypes: usertypes || [],
    });
  }

  useEffect(() => {
    fetchmasters();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [filterparams, statusFilter]);

  return (
    <Page className={classes.root} title="Subscription Users">
      <Typography variant="h4" gutterBottom>Subscription Overview</Typography>
      <div className={classes.filterRow}>
        <UserFilter
          masters={masters}
          onPrepare={onPreparedownload}
          onSearch={onSearch}
          isreadyfordownload={ispreparedownload}
          onFilter={onFilter}
          data={downloadusers}
          csvHeaders={SUBSCRIPTION_CSV_HEADERS}
          csvFilename="subscription-users"
          statusOptions={STATUS_OPTIONS}
          statusValue={statusFilter}
        />
        <FormControl variant="outlined" className={classes.filter} size="small">
          <InputLabel>Status filter</InputLabel>
          <Select
            value={statusFilter}
            onChange={e => onStatusChange(e.target.value)}
            label="Status filter"
          >
            {STATUS_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <TableContainer component={Paper} className={classes.results}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(row => (
              <TableRow key={row.user_profile_id}>
                <TableCell>{row.user?.name || row.user_name}</TableCell>
                <TableCell>{row.user?.mobilenumber || row.mobile}</TableCell>
                <TableCell>{row.plan?.name || '—'}</TableCell>
                <TableCell>
                  {row.valid_from ? moment(row.valid_from).format('DD/MM/YYYY') : '—'}
                </TableCell>
                <TableCell>
                  {row.valid_until ? moment(row.valid_until).format('DD/MM/YYYY') : '—'}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.status}
                    color={STATUS_COLORS[row.status] || 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => history.push(`/edituser/${row.user_profile_id}/1`)}
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">No subscription records found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Page>
  );
});

export default SubscriptionUsers;
